import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DoneIcon from "@mui/icons-material/Done";
import {
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Divider,
  FormHelperText,
  Link,
} from "@mui/material";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import { TextField } from "./TextField";
import LensIcon from "@mui/icons-material/Lens";
import { CountryInput } from "./CountryInput";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import Button from "../../Button";
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "./BillingForm";
import { size, upperFirst } from "lodash";
import { Context } from "../../../Store/Store";
import { StripeAPI } from "../../../Services/BillingService";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

// Validation Schema
const schema = yup.object().shape({
  name: yup.string().required("Company Name is required"),
  line1: yup.string().required("Address Line 1 is required"),
  city: yup.string().required("City is required"),
  postal_code: yup.string().required("Zip Code is required"),
  state: yup.string().required("State is required"),
  country: yup.mixed().required("Country is required"),
  vatType: yup.string().optional(),
  vatNumber: yup.string().optional(),
  cvCountry: yup.mixed().required("Country is required"),
});

const ccStyleOptions = {
  style: {
    base: {
      fontSize: "15.2px",
      color: "rgba(0, 0, 0, 0.87)",
      fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
      "::placeholder": {
        color: "#ABABAB",
        fontWeight: "normal",
      },
    },
  },
};

export const FormContainer = () => {
  // ===================== CONTEXT
  const {
    planDetails,
    formData,
    selectedPlan,
    updateSelectedPlan,
    fetchCoupon,
    couponStatus,
  } = useFormContext();
  const [state, dispatch] = useContext(Context);

  // ====================== HOOKS
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      subscription: JSON.stringify({}),
      name: "",
      ssn: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: null,
      cvCountry: null,
    },
  });
  const [searchParams] = useSearchParams();

  // ===================== STATES
  const [loading, setLoading] = useState(false);
  const [canFocus, setCanFocus] = useState(false);
  const [cardError, setCardError] = useState({
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
  });

  // =================== VARIABLES
  const plan = searchParams.get("plan");
  const discountField = watch("discount");
  const isAnnualSubscription =
    JSON.parse(watch("subscription")).type === "annually";
  const user = state?.user.data;
  const allComplete = Object.values(cardError).every(
    (field) => field?.isCompleted === true
  );
  const isButtonDisabled = !allComplete;
  const onError = () => {
    setCanFocus(true);
  };

  // ==================== EFFECTS
  useEffect(() => {
    if (errors && canFocus) {
      // Sort inputs based on their position on the page. (the order will be based on validaton order otherwise)
      const elements = Object.keys(errors)
        .map((name) => document.getElementsByName(name)[0])
        .filter((el) => !!el);
      elements.sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
      );

      if (elements.length > 0) {
        let errorElement = elements[0];
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" }); // scrollIntoView options are not supported in Safari
        errorElement.focus({ preventScroll: true });
        setCanFocus(false); // so the form doesn't suddenly jump to the next input that has error.
      }
    }
  }, [errors, canFocus]);

  useEffect(() => {
    if (formData) {
      reset({
        name: formData?.name,
        ssn: formData?.ssn,
        line1: formData?.line1,
        line2: formData?.line2,
        city: formData?.city,
        postal_code: formData?.postal_code,
        country: formData?.country || null,
        cvCountry: formData?.country || null,
        state: formData?.state,
      });
    }
  }, [reset, formData]);

  useEffect(() => {
    if (selectedPlan) {
      setValue("subscription", JSON.stringify(selectedPlan.plan));
    }
  }, [selectedPlan, setValue]);

  // ====================== HANDLER
  const onHandleCardChange = (event) => {
    if (event.error) {
      setCardError((prev) => ({
        ...prev,
        [event.elementType]: { error: event.error },
      }));
    } else {
      setCardError((prev) => ({
        ...prev,
        [event.elementType]: { isCompleted: event.complete },
      }));
    }
  };

  const onHandleApplyCoupon = async () => {
    await fetchCoupon(discountField, JSON.parse(getValues("subscription")));
  };

  const onHandleLogout = () => {
    localStorage.clear();
    dispatch({
      type: "RESET_STATE",
    });
    navigate("/Billing/Checkout/Successful");
  };

  const onHandleSubmit = async (data) => {
    const subscription = data?.subscription
      ? JSON.parse(data?.subscription)
      : null;

    if (!stripe || !elements || !subscription) {
      toast.error("Something went wrong, please contact Admin support");
      return;
    }

    setLoading(true);
    try {
      const payment = await StripeAPI({
        api_type: "payment_intent",
        email: user.email,
        type: subscription.type,
        selected_plan: upperFirst(plan),
        coupon_code: data?.discount || null,
        name: data?.name,
        address: {
          city: data?.city,
          country: data?.country?.value,
          line1: data?.line1,
          line2: data?.lin2,
          postal_code: data?.postal_code,
          state: data?.state,
        },
      });
      const paymentResult = await payment.json();
      if (paymentResult?.clientSecret) {
        const { clientSecret } = paymentResult;
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
              address: {
                country: data?.cvCountry?.value,
              },
            },
          },
        });

        if (result.error) {
          setLoading(false);
          toast.error(result.error.message);
        } else {
          if (result.paymentIntent.status === "succeeded") {
            try {
              const createSubscription = await StripeAPI({
                api_type: "create_subscription",
                email: user.email,
                selected_plan: upperFirst(plan),
              });
              const subscriptionResult = await createSubscription.json();
              if (subscriptionResult) {
                toast.success(
                  "Payment successful. Redirecting to payment success page..."
                );
                return onHandleLogout();
              } else {
                toast.error(
                  "Error creating subscription. Please contact Admin for support"
                );
                setLoading(false);
              }
            } catch (error) {
              setLoading(false);
              toast.error(
                "Error creating subscription. Please contact Admin for support"
              );
            }
          }
        }
      }
    } catch (error) {
      toast.error("Error processing. Please try again later");
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER
  const renderEndEndorment = () => {
    if (couponStatus.type === "success") {
      return (
        <Stack
          direction={"row"}
          gap={1}
          pr={3}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <DoneIcon sx={{ color: "#64BE37" }} />
          <Typography color="#64BE37" fontWeight={600}>
            Applied
          </Typography>
        </Stack>
      );
    }
    if (size(discountField) > 0) {
      return (
        <Box height={"100%"}>
          <Button
            style={{
              borderRadius: 0,
              minWidth: "9rem",
              height: "100%",
              fontWeight: 600,
            }}
            loading={couponStatus.type === "loading"}
            onClick={onHandleApplyCoupon}
          >
            Apply coupon
          </Button>
        </Box>
      );
    }
  };

  // ==================== RETURN
  return (
    <form onSubmit={handleSubmit(onHandleSubmit, onError)}>
      <Stack gap={4}>
        <Controller
          name="subscription"
          control={control}
          render={({ field }) => (
            <RadioGroup
              style={{
                display: "grid",
                gap: "1rem",
                gridTemplateColumns: "repeat(3, 1fr)",
              }}
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value);
                updateSelectedPlan(JSON.parse(e.target.value));
              }}
            >
              {planDetails.map((option) => (
                <FormControlLabel
                  key={option.type}
                  value={JSON.stringify(option)}
                  sx={{
                    width: "100%",
                    border: "1px solid #0A72C3",
                    borderRadius: ".65rem",
                    display: "flex",
                    justifyContent: "center",
                    py: 1.5,
                    marginLeft: 0,
                    gap: 1,
                    ...(option.type === JSON.parse(field.value).type && {
                      backgroundColor: "rgba(10, 114, 195, 0.10)",
                      borderWidth: "2px",
                    }),
                  }}
                  control={
                    <Radio
                      sx={{
                        color: "#0A72C3",
                        "&.Mui-checked": {
                          color: "#0A72C3",
                        },
                      }}
                      checkedIcon={<LensIcon sx={{ color: "#0A72C3" }} />}
                    />
                  }
                  label={
                    <Stack color="#0A72C3">
                      <Typography fontWeight={600}>
                        {upperFirst(option.type)}
                      </Typography>
                      <Typography fontWeight={500} fontSize={".95rem"}>
                        $ {option.price} / month
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          )}
        />
        {errors.subscription && (
          <Typography color="error">{errors.subscription.message}</Typography>
        )}

        {/** Address */}
        <Stack
          backgroundColor="#FFF"
          spacing={3}
          p={2.5}
          border="1px solid rgba(0, 0, 0, 0.10)"
          borderRadius={"1rem"}
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <FmdGoodIcon />
            <Typography fontWeight={600} color="#415058">
              Address Information
            </Typography>
          </Stack>

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                title="Name / Company Name"
                fullWidth
                isRequired
                {...field}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <Controller
            name="line1"
            control={control}
            render={({ field }) => (
              <TextField
                title="Address Line 1"
                isRequired
                placeholder="Address Line 1"
                fullWidth
                {...field}
                error={!!errors.line1}
                helperText={errors.line1?.message}
              />
            )}
          />

          <Controller
            name="line2"
            control={control}
            render={({ field }) => (
              <TextField
                title="Address Line 2"
                placeholder="Address Line 2"
                fullWidth
                {...field}
              />
            )}
          />

          <Stack direction="row" spacing={2} width={"100%"}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  title="City"
                  containerProps={{
                    width: "33.33%",
                  }}
                  placeholder="City"
                  isRequired
                  {...field}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              )}
            />
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextField
                  title="State"
                  containerProps={{
                    width: "33.33%",
                  }}
                  placeholder="State"
                  isRequired
                  {...field}
                  error={!!errors.state}
                  helperText={errors.state?.message}
                />
              )}
            />
            <Controller
              name="postal_code"
              control={control}
              render={({ field }) => (
                <TextField
                  title="Zip Code"
                  placeholder="Zip Code"
                  containerProps={{
                    width: "33.33%",
                  }}
                  fullWidth
                  isRequired
                  {...field}
                  error={!!errors.postal_code}
                  helperText={errors.postal_code?.message}
                />
              )}
            />
          </Stack>

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <CountryInput {...field} errors={errors.country} />
            )}
          />
          {/** Business registration number */}
          <Controller
            name="ssn"
            control={control}
            render={({ field }) => (
              <TextField
                title="Business Registration Number (optional)"
                fullWidth
                {...field}
                error={!!errors.ssn}
                helperText={errors.ssn?.message}
              />
            )}
          />
        </Stack>

        {/** Card */}
        <Stack
          backgroundColor="#FFF"
          spacing={3}
          p={2.5}
          border="1px solid rgba(0, 0, 0, 0.10)"
          borderRadius={"1rem"}
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <CreditCardIcon />
            <Typography fontWeight={600} color="#415058">
              Card details
            </Typography>
          </Stack>
          {/* <PaymentElement /> */}
          <Box display={"grid"} gridTemplateColumns={"repeat(5, 1fr)"} gap={2}>
            <Box
              gridColumn={"span 3"}
              display={"flex"}
              flexDirection={"column"}
              gap={1.25}
              height={"fit-content"}
            >
              <Typography color="#415058" fontWeight={500} fontSize={".95rem"}>
                Card number
              </Typography>
              <Box
                p={2}
                border={
                  cardError?.cardNumber?.error
                    ? "1px solid #d61f1f"
                    : "1px solid #E0E2E4"
                }
                borderRadius=".75rem"
                sx={{
                  ":hover": {
                    borderColor: "rgba(0, 0, 0, 0.87)",
                    cursor: "auto",
                  },
                }}
              >
                <Controller
                  name="cardNumber"
                  control={control}
                  render={({ field }) => (
                    <CardNumberElement
                      {...field}
                      options={ccStyleOptions}
                      onChange={(event) => {
                        field.onChange(event.value);
                        onHandleCardChange(event);
                      }}
                    />
                  )}
                />
              </Box>
              {cardError.cardNumber?.error && (
                <FormHelperText error sx={{ marginTop: "-8px" }}>
                  {cardError?.cardNumber?.error?.message}
                </FormHelperText>
              )}
            </Box>
            <Box
              gridColumn={"span 1"}
              display={"flex"}
              flexDirection={"column"}
              gap={1.25}
              height={"fit-content"}
            >
              <Typography color="#415058" fontWeight={500} fontSize={".95rem"}>
                Expiry date
              </Typography>

              <Box
                flex={1}
                p={2}
                border={
                  cardError?.cardExpiry?.error
                    ? "1px solid #d61f1f"
                    : "1px solid #E0E2E4"
                }
                borderRadius=".75rem"
                sx={{
                  ":hover": {
                    borderColor: "rgba(0, 0, 0, 0.87)",
                    cursor: "auto",
                  },
                }}
              >
                <Controller
                  name="cardExpiry"
                  control={control}
                  render={({ field }) => (
                    <CardExpiryElement
                      {...field}
                      options={ccStyleOptions}
                      onChange={(event) => {
                        field.onChange(event.value);
                        onHandleCardChange(event);
                      }}
                    />
                  )}
                />
              </Box>
              {cardError.cardExpiry?.error && (
                <FormHelperText error sx={{ marginTop: "-8px" }}>
                  {cardError?.cardExpiry?.error?.message}
                </FormHelperText>
              )}
            </Box>
            <Box
              gridColumn={"span 1"}
              display={"flex"}
              flexDirection={"column"}
              gap={1.25}
              height={"fit-content"}
            >
              <Typography color="#415058" fontWeight={500} fontSize={".95rem"}>
                Security code
              </Typography>

              <Box
                flex={1}
                p={2}
                border={
                  cardError?.cardCvc?.error
                    ? "1px solid #d61f1f"
                    : "1px solid #E0E2E4"
                }
                borderRadius=".75rem"
                sx={{
                  ":hover": {
                    borderColor: "rgba(0, 0, 0, 0.87)",
                    cursor: "auto",
                  },
                }}
              >
                <Controller
                  name="cardCvc"
                  control={control}
                  render={({ field }) => (
                    <CardCvcElement
                      {...field}
                      options={ccStyleOptions}
                      onChange={(event) => {
                        field.onChange(event.value);
                        onHandleCardChange(event);
                      }}
                    />
                  )}
                />
              </Box>
              {cardError?.cardCvc?.error && (
                <FormHelperText error sx={{ marginTop: "-8px" }}>
                  {cardError?.cardCvc?.error?.message}
                </FormHelperText>
              )}
            </Box>
          </Box>
          <Controller
            name="cvCountry"
            control={control}
            render={({ field }) => (
              <CountryInput {...field} errors={errors.cvCountry} />
            )}
          />
          {!isAnnualSubscription && (
            <>
              <Divider />
              <Stack>
                <Stack direction={"row"} height={"100%"}>
                  <Stack
                    backgroundColor="rgba(160, 167, 171, 0.10)"
                    border={
                      errors.discount?.message || couponStatus.error
                        ? "1px solid #d61f1f"
                        : "1px solid #E0E2E4"
                    }
                    justifyContent={"center"}
                    borderRadius={"8px 0 0 8px"}
                    p={1.85}
                  >
                    <LocalOfferIcon sx={{ color: "#0a66c2" }} />
                  </Stack>
                  <Controller
                    name="discount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        disabled={couponStatus.type === "success"}
                        containerProps={{
                          width: "100%",
                        }}
                        InputProps={{
                          sx: {
                            borderRadius: " 0 .75rem .75rem 0",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E2E4",
                            },
                            paddingRight: 0,
                            overflow: "hidden",
                          },
                          endAdornment: renderEndEndorment(),
                        }}
                        placeholder="Coupon code (optional)"
                        fullWidth
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value.toUpperCase());
                        }}
                        error={!!errors.discount || !!couponStatus.error}
                      />
                    )}
                  />
                </Stack>
                {(errors.discount?.message || couponStatus.error) && (
                  <FormHelperText error>
                    {errors.discount?.message || couponStatus.error}
                  </FormHelperText>
                )}
              </Stack>
            </>
          )}
        </Stack>
        <Stack
          direction={"row"}
          width={"100%"}
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={3}
        >
          <Link
            href="/Billing"
            style={{ cursor: "pointer", textDecoration: "none" }}
          >
            <Typography color="#0A72C3">Cancel</Typography>
          </Link>
          <Button
            startIcon={<CreditCardIcon />}
            size="large"
            type="submit"
            disabled={isButtonDisabled || loading}
            loading={loading}
            style={{ paddingBlock: ".6rem" }}
          >
            Pay ${selectedPlan?.plan?.total} now
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
